import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorText } from '../lib/api';
import { watchLive, type Interaction, type LiveApi } from '../lib/live';
import { Button, Card, CardHeader, cx, ErrorText, Input, Pill } from './ui';
import { LiveJoin } from './LiveJoin';
import type { StageLayout } from './LiveStage';

type Tab = 'chat' | 'qa' | 'hands';

/** The webinar stage with chat, Q&A and the hand queue. Works for members and guests; `live` carries the difference. */
export function WebinarRoom({ id, live, guestSession, layout, canJoinNow, joinHint, joinLabel = 'Join the webinar', host, onChange }: { id: string; live: LiveApi; guestSession?: string; layout: StageLayout; canJoinNow: boolean; joinHint?: string | null; joinLabel?: string; host?: boolean; onChange?: () => void }) {
  const qc = useQueryClient();
  const key = ['interaction', id, guestSession ? 'guest' : 'member'];
  const { data } = useQuery({ queryKey: key, queryFn: live.interaction, refetchInterval: 15_000, retry: 1 });
  useEffect(
    () =>
      watchLive({ kind: 'webinar', id, guestSession }, () => {
        void qc.invalidateQueries({ queryKey: key });
        onChange?.();
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, guestSession],
  );
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const [tab, setTab] = useState<Tab>('chat');
  const [text, setText] = useState('');
  const send = useMutation({
    mutationFn: (v: { kind: Tab; body: string }) => (v.kind === 'chat' ? live.chat(v.body) : live.ask(v.body)),
    onSuccess: () => {
      setText('');
      refresh();
    },
  });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: refresh });
  const d: Interaction | undefined = data;
  const tabs: { id: Tab; label: string }[] = [
    { id: 'chat', label: `Chat${d ? ` (${d.chat.length})` : ''}` },
    { id: 'qa', label: `Q&A${d ? ` (${d.questions.filter((q) => q.status === 'OPEN').length})` : ''}` },
    ...(d?.manager ? [{ id: 'hands' as Tab, label: `Hands${d.hands.length ? ` (${d.hands.length})` : ''}` }] : []),
  ];
  const over = d && d.status !== 'LIVE' && d.status !== 'SCHEDULED';
  return (
    <div className="grid gap-3.5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <Card className="flex min-w-0 flex-col gap-3 p-4">
        {over ? (
          <p className="m-0 text-sub">This webinar has ended.</p>
        ) : (
          <LiveJoin getToken={live.token} layout={layout} label={joinLabel} disabledReason={canJoinNow ? null : (joinHint ?? 'Not open yet.')} allowShare={!!host} onJoined={refresh} onLeft={refresh} />
        )}
        {d && !d.manager && d.status === 'LIVE' && (
          <div className="flex flex-wrap items-center gap-3 text-sm text-sub">
            {d.myHand === 'ON_STAGE' ? <Pill tone="up">You’re on stage</Pill> : d.myHand === 'RAISED' ? <Pill tone="warn">Hand raised</Pill> : null}
            {d.myHand ? <Button onClick={() => act.mutate(live.lower)}>{d.myHand === 'ON_STAGE' ? 'Step down' : 'Lower hand'}</Button> : <Button onClick={() => act.mutate(live.raise)}>Raise hand</Button>}
            {!d.myHand && 'The host can bring you on stage to speak.'}
          </div>
        )}
        <ErrorText error={act.error} />
      </Card>
      <Card className="flex min-w-0 flex-col">
        <div role="tablist" aria-label="Audience" className="flex border-b border-line">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} className={cx('min-h-11 flex-1 cursor-pointer border-0 bg-transparent px-2 text-sm font-semibold', tab === t.id ? 'border-b-2 border-accent text-ink' : 'text-sub')} style={tab === t.id ? { borderBottom: '2px solid #6aa8ff' } : undefined}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex max-h-[360px] min-h-[160px] flex-col gap-2 overflow-y-auto p-3" role="tabpanel">
          {tab === 'chat' && (
            <>
              {!d?.chat.length && <p className="m-0 text-sm text-muted">No messages yet.</p>}
              {d?.chat.map((m) => (
                <div key={m.id} className={cx('flex flex-col rounded-lg px-2.5 py-1.5 text-sm', m.mine ? 'bg-accent-bg' : 'bg-raised', m.hidden && 'opacity-60')}>
                  <span className="text-xs text-muted">
                    {m.authorName}
                    {m.host ? ' · host' : ''}
                    {m.hidden ? ' · hidden' : ''}
                  </span>
                  <span className="break-words">{m.body}</span>
                  {d.manager && live.moderateChat && (
                    <button type="button" className="min-h-8 self-start cursor-pointer border-0 bg-transparent p-0 text-xs text-accent" onClick={() => act.mutate(() => live.moderateChat!(m.id, m.hidden ? 'UNHIDE' : 'HIDE'))}>
                      {m.hidden ? 'Show' : 'Hide'}
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
          {tab === 'qa' && (
            <>
              {!d?.questions.length && <p className="m-0 text-sm text-muted">No questions yet. Ask the first one.</p>}
              {d?.questions.map((q) => (
                <div key={q.id} className={cx('flex flex-col gap-1 rounded-lg bg-raised px-2.5 py-2 text-sm', q.status !== 'OPEN' && 'opacity-70')}>
                  <span className="text-xs text-muted">
                    {q.authorName} {q.status === 'ANSWERED' && <Pill tone="up">Answered</Pill>} {q.status === 'HIDDEN' && <Pill tone="neutral">Hidden</Pill>}
                  </span>
                  <span className="break-words">{q.body}</span>
                  <span className="flex flex-wrap items-center gap-2">
                    <button type="button" aria-pressed={q.iVoted} disabled={q.status === 'HIDDEN'} onClick={() => act.mutate(() => live.vote(q.id))} className={cx('min-h-9 cursor-pointer rounded-lg border px-3 text-xs font-semibold', q.iVoted ? 'border-accent bg-accent-bg text-accent' : 'border-line2 bg-transparent text-sub')}>
                      ▲ {q.votes}
                    </button>
                    {d.manager && live.moderateQuestion && (
                      <>
                        {q.status !== 'ANSWERED' ? <Button className="min-h-9 px-3 py-1 text-xs" onClick={() => act.mutate(() => live.moderateQuestion!(q.id, 'ANSWER'))}>Mark answered</Button> : <Button className="min-h-9 px-3 py-1 text-xs" onClick={() => act.mutate(() => live.moderateQuestion!(q.id, 'REOPEN'))}>Reopen</Button>}
                        <Button variant="ghost" className="min-h-9 px-3 py-1 text-xs" onClick={() => act.mutate(() => live.moderateQuestion!(q.id, q.status === 'HIDDEN' ? 'UNHIDE' : 'HIDE'))}>{q.status === 'HIDDEN' ? 'Show' : 'Hide'}</Button>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </>
          )}
          {tab === 'hands' && d?.manager && (
            <>
              {!d.hands.length && <p className="m-0 text-sm text-muted">No raised hands.</p>}
              {d.hands.map((h) => (
                <div key={h.identity} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-raised px-2.5 py-2 text-sm">
                  <span>
                    {h.name} {h.guest && <Pill>Guest</Pill>} {h.status === 'ON_STAGE' && <Pill tone="up">On stage</Pill>}
                  </span>
                  {live.stage && <Button className="min-h-9 px-3 py-1 text-xs" variant={h.status === 'ON_STAGE' ? 'secondary' : 'primary'} onClick={() => act.mutate(() => live.stage!(h.identity, h.status !== 'ON_STAGE'))}>{h.status === 'ON_STAGE' ? 'Send back' : 'Bring on stage'}</Button>}
                </div>
              ))}
            </>
          )}
        </div>
        {tab !== 'hands' && !over && (
          <form
            className="flex gap-2 border-t border-line p-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) send.mutate({ kind: tab, body: text.trim() });
            }}
          >
            <Input aria-label={tab === 'chat' ? 'Chat message' : 'Your question'} value={text} onChange={(e) => setText(e.target.value)} placeholder={tab === 'chat' ? 'Say something…' : 'Ask a question…'} maxLength={tab === 'chat' ? 500 : 300} />
            <Button variant="primary" type="submit" disabled={send.isPending || !text.trim()}>
              Send
            </Button>
          </form>
        )}
        {send.error && <p role="alert" className="m-0 px-3 pb-3 text-xs text-down">{errorText(send.error)}</p>}
      </Card>
    </div>
  );
}
